/**
 * tm/portal-ui/GovForCompany.tsx — 팀장님 포털 업체 화면 「신청할 수 있는 지원사업」 (2026-10-18 지도사 「FP 포털에서 고객이 받을 수 있는 지원금도 보게 · 직관적이고 UI/UX 편리하게」)
 *
 *  서버 GET /api/tm/fp-portal-data/:fpId/gov/:companyId (services/fpGovReco) — 해당만 · 올해 공고만(2026-10-18) · 지난 공고 빼고 · 진행 중 업무와 같은 사업 빼고
 *  디자인 검토(2026-10-18) 반영:
 *   - 제목 「신청할 수 있는 지원사업」(「받을 수 있는」은 약속처럼 들림) · 설명 「회사 정보에 맞고 지금 올라와 있는 공고예요. 신청 전에 임지도사가 다시 확인해요」
 *   - 2026-10-18 지도사 「12건으로 국한하지 말고 지금 신청 가능한 걸 종류별로 클릭해서 · 작년 것 말고 지금 올라온 것 위주로 · 정책자금도 넣어」
 *     → 건수 제한 없음 · 종류 칩(전체 · 제품개발 · 생산 · 고용 · 마케팅 · 기타 · 정책자금, 건수 함께) · 접수 중 · 상시 · 곧 접수 · 최근 공고만
 *   - 아이콘 없이(모든 줄 같은 아이콘은 정보 없는 장식)
 *   - 배지 색은 하나 — 마감 7일 안만 노랑, 나머지(상시 · 열리는 날 · 내년)는 회색 · 배지 칸 너비 고정 · 오른쪽 맞춤
 *   - 줄을 누르면 바텀시트(모바일 · PC 같게) — 기간 · 지원 내용 · 기관 · [공고문 보기] [임지도사에게 문의](업체 이름 · 공고 이름을 미리 채움)
 *   - 비었을 때 두 갈래 — 회사 정보가 비어 판단 못 한 공고가 있으면 「회사 정보를 채우면 더 찾을 수 있어요」 + 문의 / 정말 없으면 「지금 맞는 공고가 없어요」
 *   - 맨 아래 「10/18(일) 기준」
 */
import { useEffect, useMemo, useState } from 'react';
import { AskButtons } from './contact';
import type { useContact } from './contact';
import { Badge, Button, FONT, G, Group, ListHeader, ListRow, Divider, Sheet, TextLink, clamp } from './tds';
import { GOV_TAXONOMY, canonCat, groupOf } from '../components/GovTaxonomy';
import type { BadgeSpec } from './portalLogic';
import { md } from './portalLogic';

export interface GovItem {
  id: string; title: string; amount: string; agency: string;
  kind: 'open' | 'always' | 'soon' | 'check';
  dday: number | null; start: string | null; end: string | null; link: string;
  category?: string; loan?: boolean;
}

/** 종류 — GovTaxonomy 대분류 k(제품개발 dev · 생산 prod · 고용 emp · 마케팅 mkt · 기타 etc · 정책자금 loan) · 분류가 없으면 기타 */
function kindsOf(x: GovItem): string[] {
  const out: string[] = [];
  if (x.loan) out.push('loan');
  String(x.category || '').split(',').map(t => t.trim()).filter(Boolean).forEach(c => {
    const g = groupOf(canonCat(c));
    if (g && !out.includes(g.k)) out.push(g.k);
  });
  return out.length ? out : ['etc'];
}
interface GovData { items: GovItem[]; total: number; near: number; asof: string }

/** 오른쪽 배지 — 색은 하나(마감 7일 안 노랑) */
export function govBadge(x: GovItem): BadgeSpec {
  if (x.kind === 'open' && x.dday != null) {
    if (x.dday === 0) return { text: '오늘 마감', tone: 'yellow' };
    return { text: `마감 D-${x.dday}`, tone: x.dday <= 7 ? 'yellow' : 'elephant' };
  }
  if (x.kind === 'always') return { text: '상시', tone: 'elephant' };
  if (x.kind === 'soon' && x.start) return { text: `${+x.start.slice(5, 7)}/${+x.start.slice(8, 10)} 열려요`, tone: 'elephant' };
  return { text: '기간 확인', tone: 'elephant' };
}

/** 시트의 기간 한 줄 */
function periodText(x: GovItem): string {
  if (x.kind === 'always') return '예산이 다 쓰일 때까지 받아요';
  if (x.kind === 'check') return '접수 기간은 공고문에서 확인해 주세요';
  const s = x.start ? md(x.start) : '', e = x.end ? md(x.end) : '';
  const span = s && e ? `${s} ~ ${e}` : e ? `${e}까지` : s ? `${s}부터` : '';
  return span;
}

export default function GovForCompany({ fpId, companyId, companyName, fpName, contact, m }: {
  fpId: string; companyId: string; companyName: string; fpName: string; contact: ReturnType<typeof useContact>; m: string;
}) {
  const [data, setData] = useState<GovData | null>(null);
  const [state, setState] = useState<'load' | 'ok' | 'err'>('load');
  const [pick, setPick] = useState('all');   // 종류 칩 — 'all' 또는 GovTaxonomy k
  const [sel, setSel] = useState<GovItem | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let off = false;
    setState('load'); setPick('all'); setSel(null);
    fetch(`/api/tm/fp-portal-data/${encodeURIComponent(fpId)}/gov/${encodeURIComponent(companyId)}`).then(r => r.json())
      .then((j: { success?: boolean; data?: GovData }) => { if (off) return; if (j.success && j.data) { setData(j.data); setState('ok'); } else setState('err'); })
      .catch(() => { if (!off) setState('err'); });
    return () => { off = true; };
  }, [fpId, companyId, tick]);

  const items = useMemo(() => (data ? data.items : []).map(x => ({ x, ks: kindsOf(x) })), [data]);
  const chips = useMemo(() => [{ k: 'all', name: '전체', n: items.length }]
    .concat(GOV_TAXONOMY.map(g => ({ k: g.k, name: g.name, n: items.filter(r => r.ks.includes(g.k)).length })).filter(c => c.n > 0)), [items]);
  const shown = pick === 'all' ? items : items.filter(r => r.ks.includes(pick));
  const total = data ? data.total : 0;
  const ctx = (x: GovItem | null) => `${companyName}, ${x ? x.title : '지원사업 찾기'}`;
  const sms = (x: GovItem | null) => x ? `[${companyName}] ${x.title} 건으로 문의드려요. FP ${fpName}: ` : `[${companyName}] 지원사업 찾기 위해 회사 정보 알려 드려요. FP ${fpName}: `;

  return (
    <>
      <Group m={m}>
        <ListHeader title="신청할 수 있는 지원사업" right={state === 'ok' && total > 0 ? `${total}건` : undefined} />
        <div style={{ padding: '0 20px 6px', fontSize: 15, color: G.g600, lineHeight: 1.5, wordBreak: 'keep-all' }}>회사 정보에 맞는 올해 공고예요. 신청 전에 임지도사가 다시 확인해요</div>
        {state === 'load' && <div style={{ padding: '12px 20px 16px', fontSize: 15, color: G.g600 }}>공고를 찾고 있어요</div>}
        {state === 'err' && (
          <div style={{ padding: '4px 20px 8px', fontSize: 15, color: G.g700 }}>
            공고를 불러오지 못했어요 <TextLink onClick={() => setTick(t => t + 1)} icon="r">다시 보기</TextLink>
          </div>
        )}
        {state === 'ok' && items.length === 0 && (
          <div style={{ padding: '8px 20px 12px' }}>
            {data && data.near > 0 ? (
              <>
                <div style={{ fontSize: 16, color: G.g800, lineHeight: 1.5, wordBreak: 'keep-all', marginBottom: 12 }}>회사 정보를 채우면 더 찾을 수 있어요. 업종 · 직원 수 · 매출 같은 정보를 임지도사에게 알려 주세요</div>
                <AskButtons c={contact} h={44} context={ctx(null)} smsBody={sms(null)} ask />
              </>
            ) : <div style={{ fontSize: 16, color: G.g700, lineHeight: 1.5, wordBreak: 'keep-all' }}>지금 맞는 공고가 없어요. 새 공고가 나오면 여기에 보여요</div>}
          </div>
        )}
        {state === 'ok' && chips.length > 2 && (
          <div role="tablist" aria-label="종류" style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '8px 20px 10px', scrollbarWidth: 'none' }}>
            {chips.map(c => {
              const on = pick === c.k;
              return (
                <button key={c.k} type="button" role="tab" aria-selected={on} className="tds-btn" onClick={() => setPick(c.k)}
                  style={{ flexShrink: 0, height: 36, padding: '0 14px', borderRadius: 18, border: 'none', cursor: 'pointer', fontFamily: FONT, fontSize: 15, fontWeight: on ? 700 : 500,
                    background: on ? G.g900 : G.g100, color: on ? '#fff' : G.g700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {c.name} {c.n}
                </button>
              );
            })}
          </div>
        )}
        {state === 'ok' && shown.map(({ x }, i) => (
          <div key={x.id}>
            {i > 0 && <Divider full />}
            <ListRow title={x.title} sub={[x.amount, x.agency].filter(Boolean).join(', ')} clip={false}
              right={<span style={{ width: 104, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}><Badge spec={govBadge(x)} /></span>}
              arrow onClick={() => setSel(x)} />
          </div>
        ))}
        {state === 'ok' && data && <div style={{ padding: '4px 20px 8px', fontSize: 14, color: G.g500, textAlign: 'right' }}>{md(data.asof)} 기준</div>}
      </Group>

      {sel && (
        <Sheet label={sel.title} onClose={() => setSel(null)}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0, fontSize: 20, fontWeight: 700, color: G.g900, lineHeight: 1.4, wordBreak: 'keep-all', ...clamp(3) }}>{sel.title}</div>
              <Badge spec={govBadge(sel)} />
            </div>
            <dl style={{ margin: '16px 0 20px', display: 'grid', gridTemplateColumns: '72px minmax(0,1fr)', rowGap: 10, columnGap: 12, fontSize: 16, lineHeight: 1.5 }}>
              {[['기간', periodText(sel)], ['지원 내용', sel.amount], ['기관', sel.agency]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} style={{ display: 'contents' }}>
                  <dt style={{ color: G.g600 }}>{k}</dt>
                  <dd style={{ margin: 0, color: G.g900, wordBreak: 'keep-all', overflowWrap: 'anywhere', ...clamp(4) }}>{v}</dd>
                </div>
              ))}
            </dl>
            <div style={{ fontSize: 15, color: G.g600, lineHeight: 1.5, marginBottom: 12, wordBreak: 'keep-all' }}>대표님께 안내하기 전에 임지도사에게 먼저 물어봐 주세요. 자격 조건을 다시 확인해 드려요</div>
            <div style={{ display: 'grid', gap: 8 }}>
              <AskButtons c={contact} h={48} context={ctx(sel)} smsBody={sms(sel)} ask />
              {sel.link && <Button kind="weak" h={48} onClick={() => window.open(sel.link, '_blank', 'noopener')}>공고문 보기</Button>}
              <Button kind="gray" h={48} onClick={() => setSel(null)}>닫기</Button>
            </div>
        </Sheet>
      )}
    </>
  );
}
